/** @format */

import React, { useEffect, useState } from "react";
import { CiEdit } from "react-icons/ci";
import { FaUsers, FaSearch, FaFilter, FaUserPlus } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import usePermissions from "../../../permissions/permission";
import ErrorBoundary from "../../../common/ErrorBoundary";
import { motion, AnimatePresence } from "framer-motion";

const ViewUserManagement = () => {
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const [statusFilter, setStatusFilter] = useState(""); // active / inactive
  const [typeFilter, setTypeFilter] = useState(""); // user / admin / superadmin

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [employeeNoFilter, setEmployeeNoFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    user_role: "",
    email: "",
    mobile: "",
    isInternalExternal: "No",
    user_type: "user",
  });

  // Safely handle form updates
  const handleForm = (k, v) => {
    if (typeof k === "string" && k.trim()) {
      setFormData((prev) => ({
        ...prev,
        [k]: v || "",
      }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (!API_URL) {
          console.error("API URL not configured");
          return;
        }

        const res = await fetch(`${API_URL}/v1/hris/user/roles`);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        if (Array.isArray(data)) {
          setRoles(data);
        } else if (data?.success && Array.isArray(data.data)) {
          setRoles(data.data);
        } else {
          setRoles([]);
        }
      } catch (e) {
        console.error("Error fetching roles:", e);
        toast.error("Failed to load roles");
        setRoles([]);
      }
    })();
  }, [API_URL]);

  const fetchUsers = async ({ toPage = page, toLimit = limit } = {}) => {
    setLoading(true);

    try {
      if (!API_URL) throw new Error("API URL not configured");

      const params = new URLSearchParams();
      params.set("page", toPage);
      params.set("limit", toLimit);
      params.set("search", employeeNoFilter.trim());

      if (roleFilter) params.set("role", roleFilter);
      if (statusFilter) params.set("status", statusFilter);
      if (typeFilter) params.set("type", typeFilter);

      const url = `${API_URL}/v1/hris/user/getAllUsers?${params.toString()}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();

      const usersData = data?.data || [];
      setUsers(usersData);

      setTotal(data?.totalCount || usersData.length);
      setTotalPages(data?.totalPages || 1);
      setPage(data?.page || toPage);
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error(err.message || "Error loading users");
      setUsers([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(1);
      fetchUsers({ toPage: 1, toLimit: limit });
    }, 500);

    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeNoFilter]);
  useEffect(() => {
    setPage(1);
    fetchUsers({ toPage: 1, toLimit: limit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, statusFilter, typeFilter]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  const handlePageChange = async (p) => {
    setPage(p);
    await fetchUsers({ toPage: p, toLimit: limit });
  };

  const handleLimitChange = async (val) => {
    const parsed = val === "" ? "" : Number(val);
    setLimit(parsed);
    setPage(1);
    await fetchUsers({ toPage: 1, toLimit: parsed });
  };

  const showingStart =
    typeof limit === "number" && limit > 0
      ? (page - 1) * limit + (users.length ? 1 : 0)
      : users.length
        ? 1
        : 0;

  const showingEnd =
    typeof limit === "number" && limit > 0
      ? (page - 1) * limit + users.length
      : users.length;

  const submitUser = async () => {
    try {
      const res = await fetch(`${API_URL}/v1/hris/user/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok || !data?.success)
        throw new Error(data?.message || "Failed to add user");
      toast.success("User added successfully");
      setShowModal(false);
      setFormData({
        username: "",
        password: "",
        name: "",
        user_role: "",
        email: "",
        mobile: "",
        isInternalExternal: "No",
        user_type: "user",
      });
      await fetchUsers({ toPage: 1, toLimit: limit });
      setPage(1);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Something went wrong");
    }
  };

  const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getUserTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "superadmin":
        return "bg-purple-100 text-purple-700";
      case "admin":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };
  const avatarBgClass = (seed = "") => {
    const palette = [
      "from-blue-500 to-blue-600",
      "from-purple-500 to-purple-600",
      "from-green-500 to-green-600",
      "from-pink-500 to-pink-600",
      "from-yellow-500 to-yellow-600",
      "from-teal-500 to-teal-600",
      "from-indigo-500 to-indigo-600",
    ];
    let hash = 0;
    for (let i = 0; i < seed.length; i++)
      hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;

    return palette[hash % palette.length];
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-6"
    >
      <ErrorBoundary>
        <div>
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <FaUsers className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-800 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  User Management
                </h1>
                <p className="text-gray-600">Settings / User Management</p>
              </div>
            </div>
          </motion.div>

          {/* Stats Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl p-6 mb-6 text-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm mb-1">Total Users</p>
                <p className="text-4xl font-bold">{total}</p>
              </div>
              <div className="text-6xl opacity-20">
                <FaUsers />
              </div>
            </div>
          </motion.div>

          {/* Filters Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100"
          >
            <div className="flex items-center gap-2 mb-4">
              <FaFilter className="text-blue-600" size={20} />
              <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Employee No Filter */}
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  placeholder="Employee No (e.g., EMP03438)"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                  value={employeeNoFilter}
                  onChange={(e) => setEmployeeNoFilter(e.target.value)}
                />
              </div>

              <select
                className="px-4 py-3 border-2 border-gray-200 rounded-xl"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select
                className="px-4 py-3 border-2 border-gray-200 rounded-xl"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">All Roles</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.role_name}
                  </option>
                ))}
              </select>

              <select
                className="px-4 py-3 border-2 border-gray-200 rounded-xl"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Super Admin</option>
              </select>

              {/* Action Buttons */}
            </div>
          </motion.div>

          {/* Add User Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex justify-end mb-6"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowModal(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <FaUserPlus size={20} />
              Add User
            </motion.button>
          </motion.div>

          {/* Table Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-100">
                    <th className="px-6 py-4 text-left font-semibold">
                      Employee No
                    </th>
                    <th className="px-6 py-4 text-left font-semibold">
                      Employee Name
                    </th>
                    <th className="px-6 py-4 text-left font-semibold">Email</th>
                    <th className="px-6 py-4 text-left font-semibold">Role</th>
                    <th className="px-6 py-4 text-left font-semibold">
                      User Type
                    </th>
                    <th className="px-6 py-4 text-center font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="p-12 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                            <span className="text-gray-500">
                              Loading users...
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : users.length ? (
                      users.map((u, i) => (
                        <motion.tr
                          key={`${u.employee_no || "row"}-${i}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <span className="font-semibold text-gray-800">
                              {u.employee_no || "-"}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <motion.div
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                className="relative"
                              >
                                {/* Blue ring around avatar */}
                                <div className="absolute inset-0 rounded-full ring-2 ring-blue-500 ring-offset-2" />
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg bg-gradient-to-br ${avatarBgClass(
                                    u.name || u.employee_no,
                                  )}`}
                                >
                                  {getInitials(u.name || u.employee_no)}
                                </div>
                              </motion.div>
                              <div>
                                <div className="font-semibold text-gray-800">
                                  {u.name || "-"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  ID: {u.employee_no}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            {u.email || "-"}
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                              {u.role_name || "-"}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium ${getUserTypeColor(u.user_type)}`}
                            >
                              {u.user_type || "-"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            {!permissionsLoading &&
                              hasPermission &&
                              hasPermission(10034) && (
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => {
                                    try {
                                      if (u?.employee_no) {
                                        navigate(
                                          `/edit-user/${u.employee_no}`,
                                          {
                                            state: {
                                              employee_status:
                                                u.employee_status,
                                              user_type: u.user_type,
                                              role_name: u.role_name,
                                            },
                                          },
                                        );
                                      }
                                    } catch (error) {
                                      console.error("Navigation error:", error);
                                      toast.error(
                                        "Error navigating to edit user",
                                      );
                                    }
                                  }}
                                  className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition-all shadow-md"
                                >
                                  <CiEdit size={18} />
                                </motion.button>
                              )}
                          </td>
                        </motion.tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="p-12 text-center">
                          <div className="text-gray-400 text-lg">
                            No users found
                          </div>
                        </td>
                      </tr>
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Pagination */}
          {limit !== "" && totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex justify-between items-center mt-6"
            >
              <span className="text-gray-600 font-medium">
                {total > 0
                  ? `Showing ${showingStart} to ${showingEnd} of ${total} entries`
                  : "Showing 0 entries"}
              </span>

              <div className="flex gap-2 items-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={page === 1}
                  onClick={() => handlePageChange(page - 1)}
                  className="px-4 py-2 bg-white border-2 border-gray-300 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all"
                >
                  Prev
                </motion.button>

                {(() => {
                  const maxVisible = 5;
                  let start = Math.max(1, page - Math.floor(maxVisible / 2));
                  let end = start + maxVisible - 1;

                  if (end > totalPages) {
                    end = totalPages;
                    start = Math.max(1, end - maxVisible + 1);
                  }

                  const pages = [];
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <motion.button
                        key={i}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handlePageChange(i)}
                        className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                          page === i
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                            : "bg-white border-2 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {i}
                      </motion.button>,
                    );
                  }
                  return pages;
                })()}

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={page === totalPages}
                  onClick={() => handlePageChange(page + 1)}
                  className="px-4 py-2 bg-white border-2 border-gray-300 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all"
                >
                  Next
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>

        {/* Add User Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4"
              onClick={() => setShowModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-lg rounded-xl flex items-center justify-center">
                      <FaUserPlus size={20} />
                    </div>
                    <h2 className="text-2xl font-bold">Add New User</h2>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowModal(false)}
                    className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                  >
                    <MdClose size={24} />
                  </motion.button>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Username
                      </label>
                      <input
                        placeholder="EMP03477"
                        value={formData.username}
                        onChange={(e) => handleForm("username", e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        placeholder="******"
                        value={formData.password}
                        onChange={(e) => handleForm("password", e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Name
                      </label>
                      <input
                        placeholder="your name here"
                        value={formData.name}
                        onChange={(e) => handleForm("name", e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        User Role
                      </label>
                      <select
                        value={formData.user_role}
                        onChange={(e) =>
                          handleForm("user_role", e.target.value)
                        }
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      >
                        <option value="">-- Select Role --</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.role_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        placeholder="user@example.com"
                        value={formData.email}
                        onChange={(e) => handleForm("email", e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Mobile
                      </label>
                      <input
                        placeholder="0771234567"
                        value={formData.mobile}
                        onChange={(e) => handleForm("mobile", e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Is Internal
                      </label>
                      <div className="flex gap-6 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            value="Yes"
                            checked={formData.isInternalExternal === "Yes"}
                            onChange={(e) =>
                              handleForm("isInternalExternal", e.target.value)
                            }
                            className="w-5 h-5 text-blue-600"
                          />
                          <span className="font-medium">Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            value="No"
                            checked={formData.isInternalExternal === "No"}
                            onChange={(e) =>
                              handleForm("isInternalExternal", e.target.value)
                            }
                            className="w-5 h-5 text-blue-600"
                          />
                          <span className="font-medium">No</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        User Type
                      </label>
                      <select
                        value={formData.user_type}
                        onChange={(e) =>
                          handleForm("user_type", e.target.value)
                        }
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      >
                        <option value="superadmin">Super Admin</option>
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end gap-4">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowModal(false)}
                      className="px-6 py-3 border-2 border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={submitUser}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                    >
                      Submit
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </ErrorBoundary>
    </motion.div>
  );
};

export default ViewUserManagement;
